import type { InterfaceDriverInfo } from '@interfaces/InterfaceDriverInfo.ts';
import type { InterfaceGenericMap } from '@interfaces/InterfaceGenericMap.ts';
import type { InterfaceGenericTypeSlug } from '@interfaces/InterfaceGenericTypeSlug.ts';
import { createSlice } from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import createDebouncedAsyncThunk from '@store/reducers/reducerHelpers/createDebouncedAsyncThunk.ts';
import { sliceStateCheckAndUpdate } from '@store/reducers/reducerHelpers/sliceStateCheckAndUpdate.ts';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { defaultTypeDebounceTimeMs } from '@store/reducers/reducerHelpers/thunkHelperShared.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';
import { getCompanyUsers } from '@store/reducers/usersReducer.ts';
import memoize from 'lodash-es/memoize';

import thunkPost from './reducerHelpers/thunkPost';

export interface DriverReducerState {
  driverStatusTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  driverStatusTypeColourMap: InterfaceGenericMap<string>;
  driverUuids: string[];
  driverInfo: InterfaceGenericMap<InterfaceDriverInfo>;
}

const driverStatusTypeColourMapDefault = {
  available: 'success',
  booked: 'cb_orange',
  maintenance: 'cb_red',
  'on-break': 'cb_lightblue',
  'on-leave': 'cb_lightblue',
  offline: 'cb_red',
  'on-delivery': 'cb_lightblue',
  unavailable: 'cb_red',
};

const initialState: DriverReducerState = {
  // driverStatusTypes: {},
  driverStatusTypeColourMap: driverStatusTypeColourMapDefault,
  driverUuids: [],
  driverInfo: {},
  driverStatusTypes: {},
};

export const sliceName = 'driver';

export const driverSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(meLogOut.fulfilled, () => initialState)
      .addCase(getDriverStatusTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('driverStatusTypes'))
      .addCase(getCompanyDriverUserUuid.fulfilled, (state, action) => {
        // Handle both array and object responses for driver UUIDs
        if (Array.isArray(action.payload)) {
          state.driverUuids = action.payload;
        } else if (
          action.payload &&
          typeof action.payload === 'object' &&
          'data' in action.payload &&
          Array.isArray(action.payload.data)
        ) {
          state.driverUuids = action.payload.data;
        } else {
          state.driverUuids = action.payload;
        }
      })
      .addCase(getCompanyDriverInfo.fulfilled, (state, action) => {
        // Handle both array and object responses for driver info
        if (Array.isArray(action.payload)) {
          // The payload might be an array where indices match the driverUuids array
          const driverInfoObject: InterfaceGenericMap<InterfaceDriverInfo> = {};
          state.driverUuids.forEach((uuid, index) => {
            if (action.payload[index]) {
              driverInfoObject[uuid] = action.payload[index];
            }
          });
          state.driverInfo = driverInfoObject;
        } else if (action.payload && typeof action.payload === 'object') {
          // If it's already an object, use it directly
          state.driverInfo = action.payload;
        }
      })
      .addCase(
        addCompanyDriver.fulfilled,
        sliceStateCheckAndUpdate.sliceItemElementGeneric('driverInfo', 'driverUserUuid'),
      )
      .addCase(
        updateCompanyDriver.fulfilled,
        sliceStateCheckAndUpdate.sliceItemElementGeneric('driverInfo', 'driverUserUuid'),
      )
      .addCase(deleteCompanyDriver.fulfilled, (state, action) => {
        // Remove the driver from the state
        if (action.meta.arg?.urlVarsReplaceMap) {
          const driverUserUuid = action.meta.arg.urlVarsReplaceMap['{driverUserUuid}'];
          if (driverUserUuid && state.driverInfo[driverUserUuid]) {
            delete state.driverInfo[driverUserUuid];
          }
        }
      });
  },
});

// export const { } = driverSlice.actions;

export const getDriverStatusTypes = thunkGet.get(sliceName, 'type/driver/status');
export const getCompanyDriverUserUuid = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/driver/user/uuid');
export const getCompanyDriverInfo = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/driver/info');

// Efficient endpoint to get driver users with their info in a single call
export const getCompanyDriversWithUsers = thunkGet.compUrl(
  sliceName,
  '/c/{companySlug}/admin/driver/users',
  [getCompanyUsers()], // This will also update the users state
);

export interface AddDriverFormValues {
  email: string;
  firstname: string;
  lastname: string;
  countryIsoCode: string;
  phoneNumber: string;
  licenceNumber: string;
  insuranceNumber: string;
  driverStatusTypeSlug?: string;
}

export const addCompanyDriver = thunkPost.compUrl<AddDriverFormValues>(sliceName, '/c/{companySlug}/admin/driver', [
  getCompanyUsers(),
  getCompanyDriverUserUuid(),
  getCompanyDriverInfo(),
]);

export interface FormUpdateDriverStatus {
  driverUserUuid: string;
  driverStatusTypeSlug: string;
}

export const updateCompanyDriverStatus = thunkPost.compUrl<FormUpdateDriverStatus>(
  sliceName,
  '/c/{companySlug}/admin/driver/status',
  [getCompanyDriverInfo()],
);

// ============= NEW CRUD OPERATIONS =============

// Update Driver Interface
export interface InterfaceUpdateDriverForm {
  email?: string;
  firstname?: string;
  lastname?: string;
  countryIsoCode?: string;
  phoneNumber?: string;
  licenceNumber?: string;
  insuranceNumber?: string;
  driverStatusTypeSlug?: string;
}

// Update Driver (Full Update)
export const updateCompanyDriver = thunkPut.compUrl<InterfaceUpdateDriverForm, { '{driverUserUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/driver/{driverUserUuid}',
  [getCompanyDriverInfo()],
);

// Delete Driver
export const deleteCompanyDriver = thunkDelete.compUrl<undefined, { '{driverUserUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/driver/{driverUserUuid}',
  [getCompanyDriverUserUuid(), getCompanyDriverInfo()],
);

// export const {} = driverSlice.actions;

export const selectDriverState = (state: AppState) => state.driver;
export const selectDriverStatusTypes = (state: AppState) => selectDriverState(state).driverStatusTypes;
export const selectDriverStatusTypeColourMap = (state: AppState) => selectDriverState(state).driverStatusTypeColourMap;

export const selectDriverUuids = memoize((state: AppState) => selectDriverState(state).driverUuids);
export const selectDriverInfo = memoize((state: AppState) => selectDriverState(state).driverInfo);

export default driverSlice.reducer;

// Only for initial load of data that doesn't require authentication
// This is only done to speed things up. It is not required.
// This data should be reloaded in the pages that need it.
// Load any data a page needs in the page. Else it will not update ever.
export const loadInitialDriverData = createDebouncedAsyncThunk(
  `${sliceName}/initial`,
  async (_, { dispatch }) => {
    dispatch(getDriverStatusTypes());
  },
  defaultTypeDebounceTimeMs,
);
